import React from 'react';
import get from 'lodash.get';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { catchError, mergeMap, filter, take, map, switchMap, debounceTime } from 'rxjs/operators';

import { noop } from 'ui-components/default-props/noop';
import { ModalService } from 'ui-components/services/modal-service';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_INFO,
} from 'ui-components/services/notification-service';
import { NotificationHubService } from '../notification-hub/notification-hub-service';
import { NotificationType } from '../notification-hub/notification-hub.types';
import { notificationDescriptionMessages } from '../notification-hub/notification-messages';

import * as Actions from './inventory.actions';
import * as InventoryApi from './inventory.api';
import { ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS, ALLOCATION_CHECK_FAILED } from './inventory.constants';
import { messages } from './messages';
import { InventoryAllocationErrorModal } from '../../components/modals/inventory-allocation-error-modal';
import { getActiveFulfillmentPlanIdFromOrderDetailsUrl } from '../../routes/constants';
import { DocumentService } from '../../services/document-service';
import { createLookupHash } from '../../services/hash-service';
import { InventoryService } from '../../services/inventory-service';
import * as CswActions from '../../store/csw/csw.actions';
import { entitiesLoad } from '../../store/entities/entities.actions';
import Global from '../../store/global-selectors';
import * as OrdersActions from '../../store/orders/orders.actions';
import { Workflows } from '../../tracking/tracking-constants';
import { trackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { isShipFromExternal, getSellerFulfillmentProviderIdFromShipFromId } from '../csw/csw.fulfillment-plan-to-csw';
import { SellerProperties } from '../seller/seller-properties';

const NOTIFICATION_DURATION_IN_MS = 5000;

const handleDeallocationError = () => {
  NotificationService.showNotification({
    title: <FormattedMessage {...messages.deallocationFailureTitle} />,
    content: <FormattedMessage {...messages.deallocationFailureMessage} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
  });

  return EMPTY;
};

const createGetStockLevelsArgs = (fulfillmentPlans) => {
  let itemsGroupedByInventorySourceId = fulfillmentPlans.reduce((acc, fp) => {
    const isInventorySourceASellerFulfillmentProvider = InventoryService.isFulfillmentPlanInventorySourceASellerFulfillmentProvider(fp);
    const inventorySourceId = isInventorySourceASellerFulfillmentProvider
      ? fp.externalFulfillmentConfiguration.sellerFulfillmentProviderId
      : fp.shipFromId;
    const inventorySourceIdentifierType = isInventorySourceASellerFulfillmentProvider
      ? InventoryService.InventorySourceIdentifierType.SellerFulfillmentProviderId
      : InventoryService.InventorySourceIdentifierType.ShipFromId;

    const items = fp.items
      .map((fpi) => ({
        fulfillmentPlanItemId: fpi.fulfillmentPlanItemId,
        inventoryItemIdentifier: fpi.inventoryItemIdentifier,
        sku: fpi.sku,
        fillSku: fpi.fillSku,
      }))
      .filter((item) => !!item);

    const existingInventorySourceIndex = acc.findIndex((i) => i.inventorySourceId === inventorySourceId);

    if (existingInventorySourceIndex === -1) {
      return [
        ...acc,
        {
          inventorySourceId,
          inventorySourceIdentifierType,
          items,
        },
      ];
    }

    return [
      ...acc.slice(0, existingInventorySourceIndex),
      {
        ...acc[existingInventorySourceIndex],
        items: [...acc[existingInventorySourceIndex].items, ...items],
      },
      ...acc.slice(existingInventorySourceIndex + 1),
    ];
  }, []);

  itemsGroupedByInventorySourceId = itemsGroupedByInventorySourceId.filter(
    (i) => i.inventorySourceId !== null && i.items && i.items.length,
  );
  return itemsGroupedByInventorySourceId;
};

const getStockLevelsForFulfillmentPlans = (fulfillmentPlans, state) => {
  if (!fulfillmentPlans) {
    return EMPTY;
  }

  const shipFromsForAllFulfillmentPlansOnOrder = fulfillmentPlans
    .map((fp) => Global.selectSellerShipFromsById(state, { shipFromId: fp.shipFromId }))
    .filter((shipFrom) => !!shipFrom);

  const shipFromIdsThatAreAssociatedToInventorySource = shipFromsForAllFulfillmentPlansOnOrder
    .filter((sf) => InventoryService.doesShipFromHaveInventoryTrackingEnabled(sf.shipFromId, state))
    .map((sf) => sf.shipFromId);

  const filteredFulfillmentPlans = fulfillmentPlans.filter(
    (fulfillmentPlan) =>
      shipFromIdsThatAreAssociatedToInventorySource.includes(fulfillmentPlan.shipFromId) ||
      (fulfillmentPlan.externalFulfillmentConfiguration &&
        fulfillmentPlan.externalFulfillmentConfiguration.sellerFulfillmentProviderId &&
        InventoryService.doesSellerFulfillmentProviderSupportInventoryLevelRetrieval(
          state,
          fulfillmentPlan.externalFulfillmentConfiguration.sellerFulfillmentProviderId,
        )),
  );

  if (!filteredFulfillmentPlans.length) {
    return EMPTY;
  }

  const itemsGroupedByInventorySourceId = createGetStockLevelsArgs(filteredFulfillmentPlans);
  if (itemsGroupedByInventorySourceId && itemsGroupedByInventorySourceId.length) {
    return of(Actions.getStockLevels(itemsGroupedByInventorySourceId));
  }

  return EMPTY;
};

export const getStockLevelDetails = (action$) =>
  action$.pipe(
    ofType(Actions.GET_STOCK_LEVELS_REQUEST),
    debounceTime(500),
    switchMap((action) =>
      InventoryApi.getStockLevels(action.getStockLevelsArgs).pipe(
        map((stockLevelInfo) => Actions.getStockLevelsSuccess(stockLevelInfo)),
        catchError((error) => of(Actions.getStockLevelsFailure(error))),
      ),
    ),
  );

export const getStockLevelDetailsOnGetOrderDetailsSuccess = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.STOCK_LEVEL_REQUEST_PRE_FLIGHT),
    mergeMap(({ fulfillmentPlans }) => {
      return getStockLevelsForFulfillmentPlans(fulfillmentPlans, getState());
    }),
  );
};

export const getStockLevelDetailsOnShipFromChange = (action$, { getState }) => {
  return action$.pipe(
    ofType(CswActions.CSW_UPDATE_SHIPMENT_CONFIGURATION),
    mergeMap(({ updatePayload, fulfillmentPlanId }) => {
      const state = getState();
      const shipFromId = get(updatePayload, 'shipFromId');

      if (!shipFromId) {
        return EMPTY;
      }

      const isShipFromAFulfillmentProvider = isShipFromExternal(shipFromId);
      const inventorySourceId = isShipFromAFulfillmentProvider ? getSellerFulfillmentProviderIdFromShipFromId(shipFromId) : shipFromId;

      if (isShipFromAFulfillmentProvider) {
        if (!InventoryService.doesSellerFulfillmentProviderSupportInventoryLevelRetrieval(state, inventorySourceId)) {
          return EMPTY;
        }
      } else {
        const shipFrom = Global.selectSellerShipFromsById(state, {
          shipFromId,
        });
        if (!shipFrom || !shipFrom.isAssociatedToInventorySource) {
          return EMPTY;
        }
      }

      const salesOrderIds =
        Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(state, {
          fulfillmentPlanIds: [fulfillmentPlanId],
        }) || [];
      const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansBySalesOrderIds(state, {
        salesOrderIds,
      });
      let fpItemInfo = [];

      fulfillmentPlans.forEach((fp) => {
        const thisFpItemInfo = fp.items.map((fpi) => ({
          fulfillmentPlanItemId: fpi.fulfillmentPlanItemId,
          inventoryItemIdentifier: fpi.inventoryItemIdentifier,
          sku: fpi.sku,
          fillSku: fpi.fillSku,
        }));
        fpItemInfo = [...fpItemInfo, ...thisFpItemInfo];
      });

      const itemsGroupedByInventorySourceIds = [
        {
          inventorySourceId,
          inventorySourceIdentifierType: isShipFromAFulfillmentProvider
            ? InventoryService.InventorySourceIdentifierType.SellerFulfillmentProviderId
            : InventoryService.InventorySourceIdentifierType.ShipFromId,
          items: fpItemInfo,
        },
      ];
      return of(Actions.getStockLevels(itemsGroupedByInventorySourceIds));
    }),
  );
};

export const getStockLevelDetailsOnAllocationOrDeallocationSuccess = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALLOCATE_SUCCESS, Actions.DEALLOCATE_SUCCESS),
    mergeMap(() => {
      const state = getState();
      const salesOrderId = Global.selectOrderDetailsSalesOrderId(state);
      if (!salesOrderId) {
        // only refresh stock counts if on order details
        return EMPTY;
      }

      const activeFulfillmentPlanId = getActiveFulfillmentPlanIdFromOrderDetailsUrl();
      const selectedShipFromId = Global.selectEntitiesFulfillmentPlanShipFromId(state, { fulfillmentPlanId: activeFulfillmentPlanId });
      const fulfillmentProviderId = Global.selectEntitiesFulfillmentPlanFulfillmentProviderId(state, {
        fulfillmentPlanId: activeFulfillmentPlanId,
      });
      if (!selectedShipFromId && !fulfillmentProviderId) {
        return EMPTY;
      }

      const fulfillmentPlanIds = Global.selectEntitiesSalesOrderProperty(state, { salesOrderId, property: 'fulfillmentPlanIds' });
      const fulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(state, {
          fulfillmentPlanIds,
        }) || [];

      return getStockLevelsForFulfillmentPlans(fulfillmentPlans, state);
    }),
  );
};

export const allocate = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALLOCATE),
    mergeMap(({ fulfillmentPlanIds, shipFromId, items, successAction }) => {
      const state = getState();
      const sellerSettings = Global.selectSellerSellerSettings(state);
      const { isInventoryEnabled } = new SellerProperties({ state });
      const doesShipFromHaveInventoryTrackingEnabled = InventoryService.doesShipFromHaveInventoryTrackingEnabled(shipFromId, state);

      if (!isInventoryEnabled || !doesShipFromHaveInventoryTrackingEnabled) {
        return successAction ? of(successAction) : EMPTY;
      }

      const shipIfNoStockSellerSetting = get(sellerSettings, 'Inventory:ShipNoStock');
      return InventoryApi.allocateFulfillmentPlans(fulfillmentPlanIds, shipFromId).pipe(
        mergeMap((response) => {
          const { status, inventoryAllocationCheckInfo, inventoryAllocationResults } = response;
          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS && !successAction) {
            NotificationService.showNotification({
              title: <FormattedMessage {...messages.allocationSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          }

          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS || shipIfNoStockSellerSetting === 'allow') {
            return concat(
              of(OrdersActions.silentRequestGetOrders()),
              of(Actions.allocateFulfillmentPlansSuccess(inventoryAllocationResults)),
              successAction ? of(successAction) : EMPTY,
            );
          }

          return of(
            Actions.allocateFulfillmentPlansFailure({
              inventoryAllocationCheckInfo,
              items,
              successAction,
            }),
          );
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const allocateFromOrderDetails = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALLOCATE_FROM_ORDER_DETAILS),
    mergeMap(({ fulfillmentPlanIds, successAction }) => {
      const state = getState();
      const sellerSettings = Global.selectSellerSellerSettings(state);
      const activeFulfillmentPlanId = getActiveFulfillmentPlanIdFromOrderDetailsUrl();
      const shipFromId = Global.selectEntitiesFulfillmentPlanShipFromId(state, {
        fulfillmentPlanId: activeFulfillmentPlanId,
      });
      const { isInventoryEnabled } = new SellerProperties({ state });
      const doesShipFromHaveInventoryTrackingEnabled = InventoryService.doesShipFromHaveInventoryTrackingEnabled(shipFromId, state);

      if (!isInventoryEnabled || !doesShipFromHaveInventoryTrackingEnabled) {
        return of(successAction);
      }

      const salesOrderId = Global.selectOrderDetailsSalesOrderId(state);
      const items = Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId,
        property: 'items',
      });
      const shipIfNoStockSellerSetting = get(sellerSettings, 'Inventory:ShipNoStock');
      return InventoryApi.allocateFulfillmentPlans(fulfillmentPlanIds, shipFromId).pipe(
        mergeMap((response) => {
          const { status, inventoryAllocationCheckInfo, inventoryAllocationResults } = response;
          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS && !successAction) {
            NotificationService.showNotification({
              title: <FormattedMessage {...messages.allocationSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          }

          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS || shipIfNoStockSellerSetting === 'allow') {
            return concat(
              of(OrdersActions.silentRequestGetOrders()),
              of(Actions.allocateFulfillmentPlansSuccess(inventoryAllocationResults)),
              of(successAction),
            );
          }

          return of(
            Actions.allocateFulfillmentPlansFailure({
              inventoryAllocationCheckInfo,
              items,
              successAction,
            }),
          );
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const allocateFromShipmentDetails = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALLOCATE_FROM_SHIPMENT_DETAILS),
    mergeMap(({ fulfillmentPlanId, successAction }) => {
      const state = getState();
      const sellerSettings = Global.selectSellerSellerSettings(state);
      const shipFromId = Global.selectEntitiesFulfillmentPlanShipFromId(state, {
        fulfillmentPlanId,
      });
      const { isInventoryEnabled } = new SellerProperties({ state });
      const doesShipFromHaveInventoryTrackingEnabled = InventoryService.doesShipFromHaveInventoryTrackingEnabled(shipFromId, state);

      if (!isInventoryEnabled || !doesShipFromHaveInventoryTrackingEnabled) {
        return of(successAction);
      }

      const items = Global.selectEntitiesFulfillmentPlanProperty(state, {
        fulfillmentPlanId,
        property: 'items',
      });
      const shipIfNoStockSellerSetting = get(sellerSettings, 'Inventory:ShipNoStock');
      return InventoryApi.allocateFulfillmentPlans([fulfillmentPlanId], shipFromId).pipe(
        mergeMap((response) => {
          const { status, inventoryAllocationCheckInfo, inventoryAllocationResults } = response;
          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS && !successAction) {
            NotificationService.showNotification({
              title: <FormattedMessage {...messages.allocationSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          }

          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS || shipIfNoStockSellerSetting === 'allow') {
            return concat(of(Actions.allocateFulfillmentPlansSuccess(inventoryAllocationResults)), of(successAction));
          }

          return of(
            Actions.allocateFulfillmentPlansFailure({
              inventoryAllocationCheckInfo,
              items,
              successAction,
            }),
          );
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const allocateFromOrderDetailsCsw = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALLOCATE_FROM_ORDER_DETAILS_CSW),
    mergeMap(({ fulfillmentPlanId, successAction }) => {
      const state = getState();
      const sellerSettings = Global.selectSellerSellerSettings(state);
      const { isInventoryEnabled } = new SellerProperties({ state });
      const shipFromId = Global.selectEntitiesFulfillmentPlanShipFromId(state, {
        fulfillmentPlanId,
      });
      const doesShipFromHaveInventoryTrackingEnabled = InventoryService.doesShipFromHaveInventoryTrackingEnabled(shipFromId, state);

      if (!isInventoryEnabled || !doesShipFromHaveInventoryTrackingEnabled) {
        return of(successAction);
      }

      const items = Global.selectEntitiesFulfillmentPlanProperty(state, {
        fulfillmentPlanId,
        property: 'items',
      });
      const shipIfNoStockSellerSetting = get(sellerSettings, 'Inventory:ShipNoStock');
      return InventoryApi.allocateFulfillmentPlans([fulfillmentPlanId], shipFromId).pipe(
        mergeMap((response) => {
          const { status, inventoryAllocationCheckInfo, inventoryAllocationResults } = response;
          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS && !successAction) {
            NotificationService.showNotification({
              title: <FormattedMessage {...messages.allocationSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          }

          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS || shipIfNoStockSellerSetting === 'allow') {
            return concat(
              of(OrdersActions.silentRequestGetOrders()),
              of(Actions.allocateFulfillmentPlansSuccess(inventoryAllocationResults)),
              of(successAction),
            );
          }

          return of(
            Actions.allocateFulfillmentPlansFailure({
              inventoryAllocationCheckInfo,
              items,
              successAction,
            }),
          );
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const allocateFromPrintModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALLOCATE_FROM_PRINT_MODAL),
    mergeMap(({ fulfillmentPlanIds, onSuccess }) => {
      const state = getState();
      const sellerSettings = Global.selectSellerSellerSettings(state);
      const { isInventoryEnabled } = new SellerProperties({ state });

      if (!isInventoryEnabled) {
        onSuccess();
        return EMPTY;
      }

      const items = []; // ToDo: Get items from somewhere.
      const shipIfNoStockSellerSetting = get(sellerSettings, 'Inventory:ShipNoStock');
      return InventoryApi.allocateFulfillmentPlans(fulfillmentPlanIds).pipe(
        mergeMap((response) => {
          const { status, inventoryAllocationCheckInfo, inventoryAllocationResults } = response;
          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS && !onSuccess) {
            NotificationService.showNotification({
              title: <FormattedMessage {...messages.allocationSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          }

          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS || shipIfNoStockSellerSetting === 'allow') {
            onSuccess();
            return concat(
              of(OrdersActions.silentRequestGetOrders()),
              of(Actions.allocateFulfillmentPlansSuccess(inventoryAllocationResults)),
            );
          }

          return of(
            Actions.allocateFulfillmentPlansFailure({
              inventoryAllocationCheckInfo,
              items,
              onSuccess,
            }),
          );
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

// TODO: refactor places that dispatch this action to use the component in src/components/inventory-allocation/index.js
export const allocateSelectedFulfillmentPlansFromOrdersGrid = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALLOCATE_FROM_ORDERS_GRID),
    mergeMap(({ successAction, fulfillmentPlanIds }) => {
      const state = getState();
      const sellerSettings = Global.selectSellerData(state).settings;
      const { isInventoryEnabled } = new SellerProperties({ state });

      if (!isInventoryEnabled) {
        return of(successAction);
      }

      const selectedFulfillmentPlanIds = fulfillmentPlanIds ?? Global.selectSelectedOrdersIds(state);
      const items = Global.selectEntitiesFulfillmentPlansItemsByFulfillmentPlanIds(state, {
        fulfillmentPlanIds: selectedFulfillmentPlanIds,
      });
      const shipIfNoStockSellerSetting = get(sellerSettings, 'Inventory:ShipNoStock');

      return InventoryApi.allocateFulfillmentPlans(selectedFulfillmentPlanIds).pipe(
        mergeMap((response) => {
          const { status, inventoryAllocationCheckInfo, inventoryAllocationResults } = response;
          const checkFailed = inventoryAllocationCheckInfo?.status === ALLOCATION_CHECK_FAILED;
          const results = inventoryAllocationCheckInfo?.results;
          const validatedItems = items.map((item) => {
            const allocationItem = results.find((result) => result.fulfillmentPlanItemId.toString() === item.fulfillmentPlanItemId);
            return {
              ...allocationItem,
              ...item,
            };
          });

          const allocationFailure$ = of(
            Actions.allocateFulfillmentPlansFailure({
              inventoryAllocationCheckInfo,
              items: validatedItems,
              successAction,
            }),
          );

          if (checkFailed) {
            return allocationFailure$;
          }

          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS) {
            NotificationService.showNotification({
              title: <FormattedMessage {...messages.allocationSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          }
          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS && !successAction) {
            return concat(
              of(OrdersActions.silentRequestGetOrders()),
              of(Actions.allocateFulfillmentPlansSuccess(inventoryAllocationResults)),
            );
          }

          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS || shipIfNoStockSellerSetting === 'allow') {
            return concat(
              of(OrdersActions.silentRequestGetOrders()),
              of(Actions.allocateFulfillmentPlansSuccess(response.inventoryAllocationResults)),
              !!successAction && of(successAction),
            );
          }

          return allocationFailure$;
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const allocateFulfillmentPlansFailure = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ALLOCATE_FAILURE),
    mergeMap(({ successAction, items, inventoryAllocationCheckInfo, onSuccess = noop }) => {
      const state = getState();
      const sellerSettings = Global.selectSellerData(state).settings;
      const shipIfNoStockSellerSetting = get(sellerSettings, 'Inventory:ShipNoStock');

      const inventoryLevels = inventoryAllocationCheckInfo ? inventoryAllocationCheckInfo.results : [];
      return new Observable((observer) => {
        ModalService.launchModal({
          component: InventoryAllocationErrorModal,
          props: {
            onContinue: successAction ? () => observer.next(successAction) : onSuccess,
            isWarning: shipIfNoStockSellerSetting === 'warn',
            items,
            inventoryLevels,
          },
          onClose: () => observer.complete(),
        });
      }).pipe(mergeMap((next) => of(next)));
    }),
  );
};

export const deallocate = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DEALLOCATE),
    mergeMap(({ fulfillmentPlanIds }) => {
      const state = getState();
      const { isInventoryEnabled } = new SellerProperties({ state });

      if (!isInventoryEnabled) {
        return EMPTY;
      }

      return InventoryApi.deallocateFulfillmentPlans(fulfillmentPlanIds).pipe(
        mergeMap((response) => {
          const didSucceed = response.status === 'DeallocationSuccessful';

          if (!didSucceed) {
            return handleDeallocationError();
          }

          NotificationService.showNotification({
            title: <FormattedMessage {...messages.deallocationSuccessTitle} />,
            content: <FormattedMessage {...messages.deallocationSuccessMessage} />,
            notificationType: NOTIFICATION_TYPE_SUCCESS,
          });

          return concat(of(OrdersActions.silentRequestGetOrders()), of(Actions.deallocateSuccess()));
        }),
        catchError(handleDeallocationError),
      );
    }),
  );
};

export const deallocateSilently = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DEALLOCATE_SILENTLY),
    mergeMap(({ fulfillmentPlanIds, successAction }) => {
      const state = getState();
      const { isInventoryEnabled } = new SellerProperties({ state });

      if (!isInventoryEnabled) {
        return EMPTY;
      }

      return InventoryApi.deallocateFulfillmentPlans(fulfillmentPlanIds).pipe(
        mergeMap((response) => {
          if (response.status === 'DeallocationSuccessful') {
            if (successAction) {
              return concat(of(OrdersActions.silentRequestGetOrders()), of(successAction), of(Actions.deallocateSuccess()));
            }
          }
          return EMPTY;
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const deallocateOnShipFromChange = (action$) => {
  return action$.pipe(
    ofType(CswActions.CSW_UPDATE_SHIPMENT_CONFIGURATION),
    mergeMap((action) => {
      const { fulfillmentPlanId, updatePayload } = action;

      const { shipFromId } = updatePayload;

      if (shipFromId && ['rate-browser', 'customers-create-label'].includes(fulfillmentPlanId)) {
        return of(
          Actions.deallocateFulfillmentPlans([fulfillmentPlanId], {
            type: 'NOOP',
          }),
        );
      }

      return EMPTY;
    }),
  );
};

const exportInventoryAsync = (action$, data) => {
  return InventoryApi.exportInventoryAsync(data).pipe(
    mergeMap(({ jobGuids }) => {
      const exportComplete$ = action$.pipe(
        ofType(Actions.EXPORT_INVENTORY_ASYNC_SUCCESS),
        filter((response) => jobGuids.includes(response.messageGuid)),
        take(1),
        mergeMap((response) => {
          const downloadUrl = response?.data?.DownloadUrl;
          if (downloadUrl) {
            DocumentService.downloadFile(downloadUrl);
            NotificationHubService.showNotification(
              {
                notificationType: NotificationType.INVENTORY_EXPORT_COMPLETED,
                defaultTitle: messages.inventoryExportCompleteTitle.defaultMessage,
                defaultDescription: notificationDescriptionMessages.INVENTORY_EXPORT_COMPLETED.defaultMessage,
                metadataJson: JSON.stringify({ downloadUrl }),
              },
              {
                notificationType: NOTIFICATION_TYPE_INFO,
                title: <FormattedMessage {...messages.inventoryExportCompleteTitle} />,
                content: <FormattedMessage {...messages.inventoryExportCompleteBody} />,
                duration: NOTIFICATION_DURATION_IN_MS,
              },
            );
          }
          return EMPTY;
        }),
      );
      return exportComplete$;
    }),
    catchError(() => {
      NotificationHubService.showNotification(
        {
          notificationType: NotificationType.INVENTORY_EXPORT_FAILED,
          defaultTitle: messages.inventoryExportFailureNotificationTitle.defaultMessage,
          defaultDescription: messages.inventoryExportFailureNotificationContent.defaultMessage,
        },
        {
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.inventoryExportFailureNotificationTitle} />,
          content: <FormattedMessage {...messages.inventoryExportFailureNotificationContent} />,
          duration: NOTIFICATION_DURATION_IN_MS,
        },
      );
      return EMPTY;
    }),
  );
};

export const exportInventoryRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.EXPORT_INVENTORY_REQUEST),
    mergeMap((action) => {
      const { data } = action;
      return exportInventoryAsync(action$, data);
    }),
  );
};

const showExportBeginNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.LOCATIONS_EXPORT_STARTED,
      defaultTitle: messages.inventoryLocationsExportNotificationTitle.defaultMessage,
      defaultDescription: messages.inventoryLocationsExportNotificationContent.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_INFO,
      title: <FormattedMessage {...messages.inventoryLocationsExportNotificationTitle} />,
      content: <FormattedMessage {...messages.inventoryLocationsExportNotificationContent} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

const exportLocationsInventoryAsync = (action$, data) => {
  showExportBeginNotification();
  return InventoryApi.exportLocationsInventoryAsync(data).pipe(
    mergeMap(({ jobGuids }) => {
      const exportComplete$ = action$.pipe(
        ofType(Actions.EXPORT_LOCATIONS_INVENTORY_ASYNC_SUCCESS),
        filter((response) => jobGuids.includes(response.messageGuid)),
        take(1),
        mergeMap((response) => {
          const downloadUrl = response?.data?.DownloadUrl;
          if (downloadUrl) {
            DocumentService.downloadFile(downloadUrl);
            NotificationHubService.showNotification(
              {
                notificationType: NotificationType.LOCATIONS_EXPORT_COMPLETED,
                defaultTitle: messages.locationsExportCompleteTitle.defaultMessage,
                defaultDescription: notificationDescriptionMessages.LOCATIONS_EXPORT_COMPLETED.defaultMessage,
                metadataJson: JSON.stringify({ downloadUrl }),
              },
              {
                notificationType: NOTIFICATION_TYPE_INFO,
                title: <FormattedMessage {...messages.locationsExportCompleteTitle} />,
                content: <FormattedMessage {...messages.locationsExportCompleteBody} />,
                duration: NOTIFICATION_DURATION_IN_MS,
              },
            );
          }
          return EMPTY;
        }),
      );
      return exportComplete$;
    }),
    catchError(() => {
      NotificationHubService.showNotification(
        {
          notificationType: NotificationType.LOCATIONS_EXPORT_FAILED,
          defaultTitle: messages.inventoryLocationsExportNotificationTitle.defaultMessage,
          defaultDescription: messages.inventoryLocationsExportNotificationContent.defaultMessage,
        },
        {
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.inventoryLocationsExportNotificationTitle} />,
          content: <FormattedMessage {...messages.inventoryLocationsExportNotificationContent} />,
          duration: NOTIFICATION_DURATION_IN_MS,
        },
      );
      return EMPTY;
    }),
  );
};

export const exportLocationsInventoryRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.EXPORT_LOCATIONS_INVENTORY_REQUEST),
    mergeMap((action) => {
      const { data } = action;
      return exportLocationsInventoryAsync(action$, data);
    }),
  );
};

const exportTotesAsync = (action$, data) => {
  showExportTotesBeginNotification();
  return InventoryApi.exportTotesAsync(data).pipe(
    mergeMap(({ jobGuids }) => {
      const exportComplete$ = action$.pipe(
        ofType(Actions.EXPORT_TOTES_ASYNC_SUCCESS),
        filter((response) => {
          return jobGuids.includes(response.messageGuid);
        }),
        take(1),
        mergeMap((response) => {
          const downloadUrl = response?.data?.DownloadUrl;
          if (downloadUrl) {
            DocumentService.downloadFile(downloadUrl);
            NotificationHubService.showNotification(
              {
                notificationType: NotificationType.TOTES_EXPORT_COMPLETED,
                defaultTitle: messages.totesExportCompleteTitle.defaultMessage,
                defaultDescription: notificationDescriptionMessages.TOTES_EXPORT_COMPLETED.defaultMessage,
                metadataJson: JSON.stringify({ downloadUrl }),
              },
              {
                notificationType: NOTIFICATION_TYPE_INFO,
                title: <FormattedMessage {...messages.totesExportCompleteTitle} />,
                content: <FormattedMessage {...messages.totesExportCompleteBody} />,
                duration: NOTIFICATION_DURATION_IN_MS,
              },
            );
          }
          return EMPTY;
        }),
      );
      return exportComplete$;
    }),
    catchError(() => {
      NotificationHubService.showNotification(
        {
          notificationType: NotificationType.TOTES_EXPORT_FAILED,
          defaultTitle: messages.totesExportNotificationErrorTitle.defaultMessage,
          defaultDescription: messages.totesExportNotificationErrorContent.defaultMessage,
        },
        {
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.totesExportNotificationErrorTitle} />,
          content: <FormattedMessage {...messages.totesExportNotificationErrorContent} />,
          duration: NOTIFICATION_DURATION_IN_MS,
        },
      );
      return EMPTY;
    }),
  );
};

export const exportTotesRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.EXPORT_TOTES_REQUEST),
    mergeMap((action) => {
      const { data } = action;
      return exportTotesAsync(action$, data);
    }),
  );
};

const showExportTotesBeginNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.TOTES_EXPORT_STARTED,
      defaultTitle: messages.totesExportNotificationTitle.defaultMessage,
      defaultDescription: messages.totesExportNotificationContent.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_INFO,
      title: <FormattedMessage {...messages.totesExportNotificationTitle} />,
      content: <FormattedMessage {...messages.totesExportNotificationContent} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

export const importInventoryRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.IMPORT_INVENTORY_REQUEST),
    mergeMap((action) => {
      const { data } = action;
      return InventoryApi.importInventoryFromCsv(data).pipe(
        mergeMap((apiResponseJobId) => {
          const importComplete$ = action$.pipe(
            ofType(Actions.IMPORT_INVENTORY_SUCCESS),
            filter((response) => response.data.jobId === apiResponseJobId.data.jobGuids[0]),
            take(1),
            mergeMap((response) => {
              trackWorkflow(Workflows.INVENTORY_IMPORT_FLOW, WorkflowSteps.COMPLETE, {
                fileName: data.file.name,
                isTestImport: data.isTestImport,
                warehouse: data.selectedWarehouse,
              });
              NotificationHubService.showNotification(
                {
                  notificationType: NotificationType.INVENTORY_IMPORT_COMPLETED,
                  defaultTitle: messages.inventoryImportNotificationTitle.defaultMessage,
                  defaultDescription: messages.inventoryImportNotificationContent.defaultMessage,
                },
                {
                  notificationType: NOTIFICATION_TYPE_INFO,
                  title: <FormattedMessage {...messages.inventoryImportNotificationTitle} />,
                  content: <FormattedMessage {...messages.inventoryImportNotificationContent} />,
                  duration: NOTIFICATION_DURATION_IN_MS,
                },
              );
              if (data.isTestImport) {
                return of(Actions.getImportSummary(response.data.jobId));
              }
              return EMPTY;
            }),
          );
          return importComplete$;
        }),
        catchError((error) => {
          trackWorkflow(Workflows.INVENTORY_IMPORT_FLOW, WorkflowSteps.ERROR, {
            fileName: data.file.name,
            isTestImport: data.isTestImport,
            warehouse: data.selectedWarehouse,
            status: error.response.status,
            statusText: error.response.statusText,
            errors: error.response.data.Errors,
          });
          NotificationHubService.showNotification(
            {
              notificationType: NotificationType.INVENTORY_IMPORT_FAILED,
              defaultTitle: messages.inventoryImportFailureNotificationTitle.defaultMessage,
              defaultDescription: messages.inventoryImportFailureNotificationContent.defaultMessage,
            },
            {
              notificationType: NOTIFICATION_TYPE_ERROR,
              title: <FormattedMessage {...messages.inventoryImportFailureNotificationTitle} />,
              content: <FormattedMessage {...messages.inventoryImportFailureNotificationContent} />,
              duration: NOTIFICATION_DURATION_IN_MS,
            },
          );
          return EMPTY;
        }),
      );
    }),
  );
};

export const getProductImportLog = (action$) =>
  action$.pipe(
    ofType(Actions.GET_IMPORT_SUMMARY),
    mergeMap((action) =>
      InventoryApi.getImportLogFromJobGUID(action.jobGuid).pipe(
        mergeMap((response) => of(Actions.openImportSummaryModal(response))),
        catchError(() => {
          NotificationHubService.showNotification(
            {
              notificationType: NotificationType.INVENTORY_IMPORT_FAILED,
              defaultTitle: messages.inventoryImportFailureNotificationTitle.defaultMessage,
              defaultDescription: messages.inventoryImportFailureNotificationContent.defaultMessage,
            },
            {
              notificationType: NOTIFICATION_TYPE_ERROR,
              title: <FormattedMessage {...messages.inventoryImportFailureNotificationTitle} />,
              content: <FormattedMessage {...messages.inventoryImportFailureNotificationContent} />,
            },
          );
          return EMPTY;
        }),
      ),
    ),
  );

export const reloadInventoryGrid = (action$) => {
  return action$.pipe(
    ofType(Actions.BULK_LOAD_INVENTORY_DATA),
    switchMap((action) => {
      const { pageData } = action;
      return InventoryApi.getInventoryPromise(pageData).pipe(
        mergeMap(({ inventory = [] }) => {
          const inventoryWithId = inventory.map((inv) => ({
            ...inv,
            id: `${inv.productId}_${inv.inventoryLocationId}`,
          }));
          const entitiesToLoad = {
            entities: {
              inventory: createLookupHash(inventoryWithId, 'id'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
      );
    }),
  );
};
